import { Button, stopPropagation, Text } from "@overmap-ai/blocks"
import {
	type Asset,
	type FormIdentifier,
	type FormSubmission,
	type Issue,
	type Project,
	type Stored,
	toUuidIdRecord,
	UNLABELLED_ASSET_LABEL,
	UNTITLED_ISSUE_TITLE,
} from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"
import { gray } from "@radix-ui/colors"
import { Link } from "@tanstack/react-router"
import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { cx } from "class-variance-authority"
import { useCallback, useMemo } from "react"

import { interactiveTextCva } from "../../../cva"
import { modelIconMapping } from "../../../mappings/icons"
import { sdk } from "../../../sdk"
import type { FormSubmissionData } from "../../../typings"
import { formatTimestampDate } from "../../../utils/date"
import { ProjectSelect } from "../../DropdownSelects/ProjectSelect"
import { AssetTypeIcon } from "../../Icons/AssetTypeIcon"
import { IssueTypeIcon } from "../../Icons/IssueTypeIcon"
import { LuIconWithWrapper } from "../../Icons/LuIconWithWrapper"
import { UserIcon } from "../../Icons/UserIcon"
import { AssetIndexLabel } from "../../IndexLabels/AssetIndexLabel"
import { IssueIndexLabel } from "../../IndexLabels/IssueIndex"
import { ColumnSortButton, ColumnSortIcon, RowSelectAllCheckbox, RowSelectCheckbox } from "../components"
import { getColumnDefsOfFieldSchema, getColumnDefsOfIdentifiers } from "../utils"

interface FormSubmissionTableProps {
	fieldSchema: FieldSchema
	formIdentifiers: Stored<FormIdentifier>[]
	projects?: Project[]
	issues: Stored<Issue>[]
	assets: Stored<Asset>[]
}

export const useFormSubmissionTableColumns = (config: FormSubmissionTableProps) => {
	const { fieldSchema, formIdentifiers, issues, assets, projects } = config

	const assetsById = useMemo(() => {
		return toUuidIdRecord(assets)
	}, [assets])

	const issuesById = useMemo(() => {
		return toUuidIdRecord(issues)
	}, [issues])

	return useMemo<ColumnDef<FormSubmissionData>[]>(
		() => [
			{
				id: "select",
				enableHiding: false,
				header({ table }) {
					return (
						<RowSelectAllCheckbox
							toggleAllRowsSelected={table.toggleAllRowsSelected}
							isSomeRowsSelected={table.getIsSomeRowsSelected()}
							isAllRowsSelected={table.getIsAllRowsSelected()}
						/>
					)
				},
				cell({ row }) {
					return (
						<RowSelectCheckbox isRowSelected={row.getIsSelected()} toggleRowSelected={row.toggleSelected} />
					)
				},
			},
			{
				id: "created_by",
				accessorFn: ({ formSubmission }) => formSubmission.created_by,
				meta: {
					plainHeader: "Created by",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created by
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const userId = getValue<FormSubmission["created_by"]>()

					return <UserIcon userId={userId ?? undefined} />
				},
			},
			{
				id: "submitted_at",
				sortingFn: "datetime",
				meta: {
					plainHeader: "Submitted at",
				},
				accessorFn: ({ formSubmission }) => formSubmission.submitted_at,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Submitted at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ getValue }) => {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
			...(projects
				? [
						{
							id: "project",
							meta: {
								plainHeader: "Project",
							},
							accessorFn: ({ formSubmission }) => {
								return formSubmission.project ?? null
							},
							header({ column }) {
								return (
									<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
										Project
										<ColumnSortIcon sortDirection={column.getIsSorted()} />
									</ColumnSortButton>
								)
							},
							cell: function Cell({ row }) {
								const { formSubmission } = row.original

								const selectedProject = useMemo(() => {
									return projects.find((project) => project.uuid == formSubmission.project)
								}, [formSubmission.project])

								const handleProjectChange = useCallback(
									(project?: Project) => {
										void sdk.formSubmissions.update({
											uuid: formSubmission.uuid,
											project: project?.uuid ?? null,
											organization: !project ? (selectedProject?.organization ?? null) : null,
										})
									},
									[formSubmission.uuid, selectedProject?.organization],
								)

								return (
									<ProjectSelect
										required={false}
										projects={projects}
										value={selectedProject}
										onValueChange={handleProjectChange}
										modal
										size="sm"
									>
										<Button
											size="sm"
											accentColor="base"
											variant="ghost"
											onClick={stopPropagation}
											disabled={!!formSubmission.issue || !!formSubmission.asset}
										>
											{selectedProject?.name ?? "None"}
										</Button>
									</ProjectSelect>
								)
							},
						},
					]
				: []),
			{
				id: "linked_to",
				meta: {
					plainHeader: "Linked to",
				},
				accessorFn: ({ formSubmission }) => {
					return formSubmission.asset ?? formSubmission.issue ?? null
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Linked to
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ row }) => {
					const { formSubmission } = row.original

					if (formSubmission.asset) {
						const asset: Stored<Asset> | undefined = assetsById[formSubmission.asset]

						return (
							<Text accentColor="base">
								{asset ? (
									<AssetTypeIcon assetTypeId={asset.asset_type} className="align-sub" />
								) : (
									<LuIconWithWrapper
										icon={modelIconMapping.assetType}
										background
										color={gray.gray9}
										className="align-sub"
									/>
								)}
								&nbsp;
								<Link
									className={cx("min-w-0", interactiveTextCva())}
									onClick={stopPropagation}
									from="/$organizationId"
									to="/$organizationId/$projectId/assets/$assetId"
									params={(prev) => ({
										...prev,
										projectId: formSubmission.project!,
										assetId: formSubmission.asset!,
									})}
								>
									{asset ? (
										<>
											<AssetIndexLabel assetId={asset.uuid} highContrast />
											&nbsp;
											<Text className="truncate">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
										</>
									) : (
										<Text className="truncate">Asset</Text>
									)}
								</Link>
							</Text>
						)
					}

					if (formSubmission.issue) {
						const issue: Stored<Issue> | undefined = issuesById[formSubmission.issue]

						return (
							<Text accentColor="base">
								{issue ? (
									<IssueTypeIcon issueTypeId={issue.issue_type} className="align-sub" />
								) : (
									<LuIconWithWrapper
										icon={modelIconMapping.issue}
										background
										color={gray.gray9}
										className="align-sub"
									/>
								)}
								&nbsp;
								<Link
									className={cx("min-w-0", interactiveTextCva())}
									onClick={stopPropagation}
									from="/$organizationId"
									to="/$organizationId/$projectId/issues/$issueId"
									params={(prev) => ({
										...prev,
										projectId: formSubmission.project!,
										issueId: formSubmission.issue!,
									})}
								>
									{issue ? (
										<>
											<IssueIndexLabel issueId={issue.uuid} highContrast />
											&nbsp;
											<Text className="truncate">{issue.title || UNTITLED_ISSUE_TITLE}</Text>
										</>
									) : (
										<Text className="truncate">Issue</Text>
									)}
								</Link>
							</Text>
						)
					}

					return <Text accentColor="base">None</Text>
				},
			},
			...getColumnDefsOfIdentifiers<Stored<FormIdentifier>, FormSubmissionData>(
				formIdentifiers,
				(identifier) =>
					({ formIdentifierValues }) =>
						formIdentifierValues[identifier.uuid]?.value,
			),

			...getColumnDefsOfFieldSchema<FormSubmissionData>(fieldSchema, ({ formSubmissionFieldValues }) =>
				fieldSchema.deserializeValues(formSubmissionFieldValues?.values ?? {}),
			),
		],
		[fieldSchema, formIdentifiers, issuesById, assetsById, projects],
	)
}

export const useFormSubmissionTable = (args: Omit<TableOptions<FormSubmissionData>, "getCoreRowModel">) => {
	return useReactTable<FormSubmissionData>({
		...args,
		getRowId: ({ formSubmission }) => formSubmission.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	})
}
